import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ChevronLeft, Cog, Settings, Car, Lightbulb, Disc, Wrench, BatteryCharging, CircleDot, Snowflake, Square, ArrowLeft, Store, PackagePlus, HandCoins } from "lucide-react";
import { api } from "@/lib/api";
import { PartCard, YardCard, Loader } from "@/components/shared";
import { LocationBar } from "@/components/LocationBar";
import { useLocationCtx } from "@/context/LocationContext";

const ICONS = { cog: Cog, settings: Settings, car: Car, lightbulb: Lightbulb, disc: Disc, wrench: Wrench, "battery-charging": BatteryCharging, "circle-dot": CircleDot, snowflake: Snowflake, square: Square };

export default function Home() {
  const nav = useNavigate();
  const [cities, setCities] = useState([]);
  const [makes, setMakes] = useState([]);
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("جدة");
  const { coords } = useLocationCtx();

  useEffect(() => {
    Promise.all([
      api.get("/cities"), api.get("/vehicles/makes"), api.get("/categories"),
      api.get("/parts", { params: { featured: true, limit: 8 } }),
      api.get("/parts", { params: { sort: "newest", limit: 8 } }),
    ]).then(([c, m, cat, f, r]) => {
      setCities(c.data); setMakes(m.data); setCats(cat.data);
      setFeatured(f.data.items); setRecent(r.data.items);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = coords
      ? { sort: "nearest", lat: coords.lat, lng: coords.lng }
      : { city: "جدة" };
    api.get("/yards", { params }).then(({ data }) => setYards(data));
  }, [coords]);

  const doSearch = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) p.set("city", city);
    nav(`/search?${p.toString()}`);
  };

  if (loading) return <Loader />;

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1637947112681-17485e3a32ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,15,23,0.75), rgba(11,15,23,0.98))" }} />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--amber)]/40 bg-[var(--amber)]/10 px-4 py-1.5 text-xs font-bold text-[var(--amber)] mb-5">
              🚗 تبدأ من جدة · توسّع لكل مدن المملكة
            </span>
            <h1 className="font-head text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight">
              لا تتعب نفسك.. قطعتك أقرب مما تتوقع
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[var(--text-muted)] leading-relaxed">
              ابحث بين آلاف قطع الغيار المستعملة من تشاليح جدة المعتمدة، قارن الأسعار، واطلب بثقة مع الدفع عند الاستلام.
            </p>

            <div className="mt-8 ts-card p-4 sm:p-5 amber-glow">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 flex items-center gap-2 rounded-xl bg-[var(--bg-secondary)] px-4">
                  <Search size={20} className="text-[var(--text-muted)]" />
                  <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    placeholder="اسم القطعة أو رقمها (مثال: مكينة كامري)"
                    className="w-full bg-transparent py-3.5 text-white outline-none placeholder:text-[var(--text-subtle)]" />
                </div>
                <select data-testid="hero-city-select" value={city} onChange={(e) => setCity(e.target.value)}
                  className="rounded-xl bg-[var(--bg-secondary)] px-4 py-3.5 text-white outline-none">
                  {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <button data-testid="hero-search-button" onClick={doSearch} className="btn-amber justify-center">
                  <Search size={18} /> بحث
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {makes.slice(0, 6).map((m) => (
                  <button key={m.id} data-testid={`hero-make-${m.id}`} onClick={() => nav(`/search?make=${encodeURIComponent(m.name)}`)}
                    className="rounded-lg border border-[var(--border-c)] px-3 py-1.5 text-xs text-[var(--text-muted)] hover:border-[var(--amber)] hover:text-[var(--amber)] transition-colors">
                    {m.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO PATHS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-14 pb-2">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="ts-card p-6 flex flex-col" data-testid="path-buy">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--amber)]/10 text-[var(--amber)] mb-4"><Search size={24} /></span>
            <h3 className="font-head text-lg font-black text-white mb-1">القطعة موجودة؟ اشترِها مباشرة</h3>
            <p className="text-sm text-[var(--text-muted)] leading-relaxed flex-1">ابحث في مخزون التشاليح المتوفر فعليًا، شاهد الصور والسعر والحالة، واشترِ فورًا.</p>
            <button onClick={() => nav("/search")} data-testid="path-buy-btn" className="btn-amber justify-center mt-4"><Search size={18} /> ابحث عن قطعة</button>
          </div>
          <div className="ts-card p-6 flex flex-col" data-testid="path-request">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--amber)]/10 text-[var(--amber)] mb-4"><PackagePlus size={24} /></span>
            <h3 className="font-head text-lg font-black text-white mb-1">ما لقيت القطعة؟ اطلبها</h3>
            <p className="text-sm text-[var(--text-muted)] leading-relaxed flex-1">أرسل طلبك للتشاليح المناسبة، استقبل عدة عروض بأسعار مختلفة، قارن واختر الأنسب لك.</p>
            <button onClick={() => nav("/request-part")} data-testid="path-request-btn"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--amber)] py-3 font-bold text-[var(--amber)] hover:bg-[var(--amber)]/10 transition-colors"><PackagePlus size={18} /> اطلب قطعة</button>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <SectionHead title="تصفح حسب القسم" link="/search" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {cats.map((c) => {
            const Icon = ICONS[c.icon] || Wrench;
            return (
              <button key={c.id} data-testid={`category-${c.id}`} onClick={() => nav(`/search?category_id=${c.id}`)}
                className="ts-card p-5 flex flex-col items-center gap-3 text-center group">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--amber)]/10 text-[var(--amber)] group-hover:bg-[var(--amber)] group-hover:text-[#0B0F17] transition-colors">
                  <Icon size={26} />
                </span>
                <span className="text-sm font-bold text-white leading-tight">{c.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
          <SectionHead title="قطع مميزة" link="/search" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {featured.map((p) => <PartCard key={p.id} part={p} />)}
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <div className="ts-card p-8 sm:p-12">
          <h2 className="font-head text-2xl sm:text-3xl font-black text-white text-center mb-10">كيف تعمل مخزون كار؟</h2>
          <div className="grid gap-8 md:grid-cols-3">
            {[
              { icon: Search, t: "١. ابحث عن القطعة", d: "اكتب اسم القطعة أو رقمها أو اختر سيارتك وابحث في تشاليح مدينتك." },
              { icon: Store, t: "٢. قارن وتواصل", d: "قارن الأسعار وحالة القطع بين التشاليح وتواصل مع البائع مباشرة." },
              { icon: HandCoins, t: "٣. اطلب وادفع عند الاستلام", d: "أرسل طلبك، جهّزه التشليح، واستلم قطعتك وادفع مباشرة للبائع." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--amber)]/10 text-[var(--amber)] mb-4"><s.icon size={30} /></span>
                <h3 className="font-bold text-white text-lg mb-2">{s.t}</h3>
                <p className="text-sm text-[var(--text-muted)] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YARDS */}
      {yards.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
          <div className="mb-4"><LocationBar /></div>
          <SectionHead title={coords ? "التشاليح الأقرب إليك" : "تشاليح جدة المعتمدة"} link="/yards" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {yards.slice(0, 6).map((y) => <YardCard key={y.id} yard={y} />)}
          </div>
        </section>
      )}

      {/* RECENT */}
      {recent.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
          <SectionHead title="أضيفت حديثًا" link="/search?sort=newest" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {recent.map((p) => <PartCard key={p.id} part={p} />)}
          </div>
        </section>
      )}

      {/* JOIN SELLER CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <div className="relative overflow-hidden rounded-3xl p-8 sm:p-14 text-center" style={{ background: "linear-gradient(120deg, #1E2738, #0B0F17)" }}>
          <div className="grain absolute inset-0" />
          <div className="relative">
            <PackagePlus size={44} className="mx-auto text-[var(--amber)] mb-4" />
            <h2 className="font-head text-2xl sm:text-4xl font-black text-white mb-3">عندك تشليح؟ بِع قطعك عبر مخزون كار</h2>
            <p className="text-[var(--text-muted)] max-w-xl mx-auto mb-6">انضم مجانًا بدون أي عمولة على مبيعاتك. قيمة القطعة كاملة تعود لك. سجّل تشليحك اليوم.</p>
            <Link to="/join-seller" data-testid="cta-join-seller" className="btn-amber inline-flex">
              سجّل تشليحك مجانًا <ArrowLeft size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHead({ title, link }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h2 className="font-head text-xl sm:text-2xl font-black text-white">{title}</h2>
      <Link to={link} className="flex items-center gap-1 text-sm text-[var(--amber)] hover:gap-2 transition-all">
        عرض الكل <ChevronLeft size={16} />
      </Link>
    </div>
  );
}
